import React, { useEffect, useState, useCallback } from 'react';
import { Heart, Brain, Edit3, RotateCcw, Sparkles, Moon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * DailyCheckinCard — the unified "Body & Mind" check-in (Scoring V2).
 *
 * Two titled sections:
 *   MIND → Mental Readiness pillar (mood, stress, overwhelmed, motivated,
 *          focus, sharpness, capability, confidence)
 *   BODY → Recovery pillar (sleep hours, sleep quality, fatigue, soreness,
 *          energy, pain) + hunger for Nutrition.
 *
 * Mood, stress and sleep quality are required; everything else is optional and
 * fills in the richer V2 sub-scores. After submit it shows a snapshot + the
 * resulting composite Wellness IQ tone and fires `wellness:refresh`.
 */

// 1-5 scales: [low-anchor, high-anchor]. `invert` only affects helper copy.
const MOOD       = ['rough', 'great'];
const STRESS     = ['calm', 'very stressed'];
const MOTIVATED  = ['low', 'high'];
const FOCUS      = ['scattered', 'locked in'];
const SHARPNESS  = ['foggy', 'sharp'];
const CAPABILITY = ['not at all', 'fully'];
const CONFIDENCE = ['low', 'high'];
const SLEEPQ     = ['awful', 'great'];
const FATIGUE    = ['fresh', 'wiped'];
const SORENESS   = ['none', 'very sore'];

const SLEEP_HOURS = [4, 5, 6, 7, 8, 9];
const ENERGY = ['drained', 'buzzing'];

// 1-5 scale row with anchor labels + optional helper line.
const ScaleRow = ({ label, anchors, helper, color, value, onChange, testIdPrefix }) => (
  <div className="space-y-1" data-testid={`row-${testIdPrefix}`}>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[11px] uppercase tracking-[0.16em] font-secondary font-bold" style={{ color }}>{label}</span>
      {helper && <span className="text-[9px] text-zinc-500 normal-case tracking-normal truncate">{helper}</span>}
    </div>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`flex-1 py-1.5 rounded-sm text-[12px] font-secondary font-bold border transition-colors
            ${value === n ? 'text-black border-transparent' : 'text-zinc-400 border-zinc-700 hover:border-zinc-500 hover:text-white'}`}
          style={value === n ? { background: color, borderColor: color } : undefined}
          data-testid={`opt-${testIdPrefix}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
    <div className="flex justify-between text-[9px] text-zinc-600 px-0.5">
      <span>{anchors[0]}</span><span>{anchors[1]}</span>
    </div>
  </div>
);

// String / number chip row.
const ChipRow = ({ icon: Icon, label, color, value, options, onChange, testIdPrefix, suffix }) => (
  <div className="space-y-1.5" data-testid={`row-${testIdPrefix}`}>
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
      <span className="text-[11px] uppercase tracking-[0.16em] font-secondary font-bold" style={{ color }}>{label}</span>
    </div>
    <div className="flex gap-1.5 flex-wrap">
      {options.map(opt => {
        const v = typeof opt === 'object' ? opt.v : opt;
        const l = typeof opt === 'object' ? opt.l : `${opt}${suffix || ''}`;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`px-2.5 py-1 rounded-sm text-[11px] font-secondary font-bold uppercase tracking-wider border transition-colors
              ${value === v ? 'text-black border-transparent' : 'text-zinc-400 border-zinc-700 hover:border-zinc-500 hover:text-white'}`}
            style={value === v ? { background: color, borderColor: color } : undefined}
            data-testid={`opt-${testIdPrefix}-${v}`}
          >
            {l}
          </button>
        );
      })}
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, label, color }) => (
  <div className="flex items-center gap-2 pt-1">
    <Icon className="w-4 h-4" style={{ color }} />
    <span className="text-[10px] uppercase tracking-[0.22em] font-secondary font-bold" style={{ color }}>{label}</span>
    <span className="flex-1 h-px bg-zinc-800" />
  </div>
);

const compositeBucket = (s) => {
  if (s == null) return null;
  if (s >= 80) return { label: 'thriving',   tone: '#a3e635', note: "You're in a good rhythm — keep what's working." };
  if (s >= 65) return { label: 'steady',     tone: '#CCFF00', note: 'Solid baseline today. Small adds compound.' };
  if (s >= 45) return { label: 'mixed',      tone: '#facc15', note: 'Some signals are heavier than others. Be gentle with yourself today.' };
  if (s >= 25) return { label: 'struggling', tone: '#fb923c', note: 'Tough patch — lean into rest, mobility, and reach out if you need to.' };
  return       { label: 'low',               tone: '#f87171', note: 'Heavy day. Skip hard load. Try 5 min of physiological-sigh breathing.' };
};

const Pill = ({ label, v, color }) => (
  <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-1.5">
    <p className="text-[8px] uppercase tracking-wider text-zinc-500">{label}</p>
    <p className="text-xs font-secondary font-bold capitalize" style={{ color }}>{v != null && v !== '' ? v : '—'}</p>
  </div>
);

const SnapshotView = ({ checkin, wellnessIQ, onEdit, onLogAgain }) => {
  const bucket = compositeBucket(wellnessIQ);
  return (
    <div className="space-y-3" data-testid="checkin-snapshot">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-accent-purple font-secondary font-bold">BODY &amp; MIND</p>
          <p className="text-xs text-zinc-500">{new Date(checkin.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
        {bucket && (
          <div className="text-right" data-testid="snapshot-tone">
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">Today</p>
            <p className="font-secondary font-bold text-sm uppercase tracking-wider" style={{ color: bucket.tone }}>{bucket.label}</p>
          </div>
        )}
      </div>
      {bucket?.note && (
        <p className="text-xs text-zinc-300 leading-relaxed border-l-2 pl-3" style={{ borderColor: bucket.tone }}>{bucket.note}</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        {checkin.mood            != null && <Pill label="Mood"     v={MOOD[0] && checkin.mood} color="#a855f7" />}
        {checkin.stress          != null && <Pill label="Stress"   v={checkin.stress} color="#a855f7" />}
        {checkin.focus           != null && <Pill label="Focus"    v={checkin.focus} color="#a855f7" />}
        {checkin.sleep_quality   != null && <Pill label="Sleep"    v={checkin.sleep_quality} color="#22d3ee" />}
        {checkin.fatigue         != null && <Pill label="Fatigue"  v={checkin.fatigue} color="#22d3ee" />}
        {checkin.muscle_soreness != null && <Pill label="Soreness" v={checkin.muscle_soreness} color="#22d3ee" />}
        {typeof checkin.body_energy === 'number' && <Pill label="Energy"   v={checkin.body_energy} color="#CCFF00" />}
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onEdit} className="flex-1 px-3 py-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 rounded-sm text-xs font-secondary font-bold uppercase tracking-wider flex items-center justify-center gap-1.5" data-testid="checkin-edit">
          <Edit3 className="w-3 h-3" /> Edit
        </button>
        <button onClick={onLogAgain} className="flex-1 px-3 py-2 bg-primary text-black rounded-sm text-xs font-secondary font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 hover:bg-primary/90" data-testid="checkin-log-again">
          <RotateCcw className="w-3 h-3" /> Log again
        </button>
      </div>
    </div>
  );
};

const MIND = '#a855f7';
const BODY = '#22d3ee';

const DailyCheckinCard = ({ initialPrefill, forceForm }) => {
  const [mode, setMode] = useState('loading');     // loading | form | snapshot
  const [checkin, setCheckin] = useState(null);
  const [wellnessIQ, setWellnessIQ] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    mood: null, stress: null, overwhelmed: null, motivated: null, focus: null, sharpness: null,
    capability: null, confidence: null,
    sleep_quality: null, sleep_hours: null, fatigue: null, muscle_soreness: null,
    body_energy: null,
    ...(initialPrefill || {}),
  });
  const set = (k) => (v) => setF(prev => ({ ...prev, [k]: v }));

  const fill = useCallback((src) => {
    setF({
      mood: src?.mood ?? null, stress: src?.stress ?? null, overwhelmed: src?.overwhelmed ?? null,
      motivated: src?.motivated ?? null, focus: src?.focus ?? null, sharpness: src?.sharpness ?? null,
      capability: src?.capability ?? null, confidence: src?.confidence ?? null,
      sleep_quality: src?.sleep_quality ?? null, sleep_hours: src?.sleep_hours ?? null,
      fatigue: src?.fatigue ?? null, muscle_soreness: src?.muscle_soreness ?? null,
      // legacy string energy (low/medium/high) → null so the new 1-5 slider starts clean
      body_energy: typeof src?.body_energy === 'number' ? src.body_energy : null,
    });
  }, []);

  const fetchToday = useCallback(async () => {
    try {
      const [todayRes, iqRes] = await Promise.all([
        api.getTodayCheckin(),
        api.getWellnessIQ().catch(() => null),
      ]);
      const today = todayRes.data?.checkin;
      setCheckin(today || null);
      setWellnessIQ(iqRes?.data?.wellness_iq ?? null);
      if (today && !forceForm) {
        setMode('snapshot');
      } else {
        if (today) fill(today);
        setMode('form');
      }
    } catch {
      setMode('form');
    }
  }, [forceForm, fill]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const canSave = f.mood != null && f.stress != null && f.sleep_quality != null;

  const handleSave = async () => {
    if (!canSave) { toast.error('Tap mood, stress and sleep quality at least.'); return; }
    setSaving(true);
    try {
      const res = await api.submitCheckin({ ...f });
      setCheckin(res.data.checkin);
      try {
        const iqRes = await api.getWellnessIQ();
        setWellnessIQ(iqRes.data?.wellness_iq ?? null);
      } catch {}
      toast.success('Logged — Nikki has it.');
      setMode('snapshot');
      window.dispatchEvent(new Event('wellness:refresh'));
    } catch {
      toast.error('Could not save. Try again?');
    } finally {
      setSaving(false);
    }
  };

  const openForm = (blank = false) => {
    if (blank) fill(null);
    else if (checkin) fill(checkin);
    setMode('form');
  };

  if (mode === 'loading') {
    return <div className="hpme-card p-5 animate-pulse" data-testid="daily-checkin-card"><div className="hpme-card-corner" /><p className="text-zinc-500 text-sm">Loading check-in…</p></div>;
  }

  return (
    <div className="hpme-card p-5" data-testid="daily-checkin-card">
      <div className="hpme-card-corner" />
      {mode === 'snapshot' ? (
        <SnapshotView checkin={checkin} wellnessIQ={wellnessIQ} onEdit={() => openForm(false)} onLogAgain={() => openForm(true)} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent-purple" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-accent-purple font-secondary font-bold">BODY &amp; MIND</span>
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed">Two quick sections. Nikki uses this to tune your movement, recovery and Mental Readiness.</p>

          {/* ── MIND ── */}
          <SectionHeader icon={Brain} label="Mind · Mental Readiness" color={MIND} />
          <ScaleRow label="Mood"       anchors={MOOD}       color={MIND} value={f.mood}        onChange={set('mood')}        testIdPrefix="mood" />
          <ScaleRow label="Stress"     anchors={STRESS}     color={MIND} value={f.stress}      onChange={set('stress')}      testIdPrefix="stress" helper="higher = more stressed" />
          <ScaleRow label="Motivated"  anchors={MOTIVATED}  color={MIND} value={f.motivated}   onChange={set('motivated')}   testIdPrefix="motivated" />
          <ScaleRow label="Focus"      anchors={FOCUS}      color={MIND} value={f.focus}       onChange={set('focus')}       testIdPrefix="focus" />
          <ScaleRow label="Sharpness"  anchors={SHARPNESS}  color={MIND} value={f.sharpness}   onChange={set('sharpness')}   testIdPrefix="sharpness" helper="mentally sharp vs foggy" />
          <ScaleRow label="Capability" anchors={CAPABILITY} color={MIND} value={f.capability}  onChange={set('capability')}  testIdPrefix="capability" helper="handling challenges today" />
          <ScaleRow label="Confidence" anchors={CONFIDENCE} color={MIND} value={f.confidence}  onChange={set('confidence')}  testIdPrefix="confidence" />

          {/* ── BODY ── */}
          <SectionHeader icon={Heart} label="Body · Recovery" color={BODY} />
          <ChipRow icon={Moon} label="Sleep hours" color={BODY} value={f.sleep_hours} options={SLEEP_HOURS} onChange={set('sleep_hours')} testIdPrefix="sleep-hours" suffix="h" />
          <ScaleRow label="Sleep quality" anchors={SLEEPQ}   color={BODY} value={f.sleep_quality}   onChange={set('sleep_quality')}   testIdPrefix="sleep" />
          <ScaleRow label="Fatigue"       anchors={FATIGUE}  color={BODY} value={f.fatigue}         onChange={set('fatigue')}         testIdPrefix="fatigue"  helper="whole-body tiredness" />
          <ScaleRow label="Soreness"      anchors={SORENESS} color={BODY} value={f.muscle_soreness} onChange={set('muscle_soreness')} testIdPrefix="soreness" helper="muscle ache / niggles from training" />
          <ScaleRow label="Energy"        anchors={ENERGY}   color="#CCFF00" value={f.body_energy}   onChange={set('body_energy')}     testIdPrefix="energy"   helper="how much gas in the tank" />

          <div className="flex gap-2 pt-1">
            {checkin && (
              <button onClick={() => setMode('snapshot')} className="px-3 py-2 border border-zinc-700 text-zinc-400 hover:text-white rounded-sm text-xs font-secondary font-bold uppercase tracking-wider" data-testid="checkin-cancel">cancel</button>
            )}
            <button onClick={handleSave} disabled={!canSave || saving} className="flex-1 px-4 py-2 bg-primary text-black rounded-sm text-xs font-secondary font-bold uppercase tracking-wider hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="checkin-save">
              {saving ? 'saving…' : (checkin ? 'log again' : 'save check-in')}
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 text-center">Mood, stress &amp; sleep quality are required. Everything else sharpens your scores.</p>
        </div>
      )}
    </div>
  );
};

export default DailyCheckinCard;
